import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AppleSignInButton from '../components/AppleSignInButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    platform: 'cloudgm'
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('expired') === '1') {
      const timer = setTimeout(() => {
        toast.error(isAr ? 'انتهت صلاحية جلستك، يرجى تسجيل الدخول من جديد' : 'Your session has expired, please log in again', { id: 'session-expired', duration: 6000 });
      }, 300);
      return () => clearTimeout(timer);
    }
    if (searchParams.get('banned') === '1') {
      const timer = setTimeout(() => {
        toast.error(
          isAr
            ? 'تم حظر حسابك من قِبَل الإدارة. يرجى التواصل مع الدعم.'
            : 'Your account has been banned. Please contact support.',
          { id: 'account-banned', duration: 8000 }
        );
      }, 300);
      return () => clearTimeout(timer);
    }
    if (searchParams.get('deleted') === '1') {
      const timer = setTimeout(() => {
        toast.error(
          isAr
            ? 'تم حذف هذا الحساب. يرجى التواصل مع الدعم إذا كان هذا خطأً.'
            : 'This account has been deleted. Contact support if you believe this is an error.',
          { id: 'account-deleted', duration: 8000 }
        );
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success(t('auth.login') + ' ✓');
      const next = sessionStorage.getItem('post_login_redirect');
      sessionStorage.removeItem('post_login_redirect');
      window.location.href = next || '/';
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Banned or archived — pretty message with correct language
      if (detail && typeof detail === 'object' && (detail.code === 'USER_BANNED' || detail.code === 'USER_ARCHIVED')) {
        toast.error(isAr ? detail.message_ar : detail.message_en, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (data) => {
    toast.success(isAr ? 'مرحباً بك!' : 'Welcome!');
    // Small delay so localStorage settles before navigate
    setTimeout(() => {
      window.location.href = '/';
    }, 300);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#f8fafc]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 start-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'} rounded-full blur-[100px]`} />
        <div className={`absolute bottom-20 end-1/4 w-[300px] h-[300px] ${isDarkMode ? 'bg-[#F59E0B]/10' : 'bg-[#F59E0B]/5'} rounded-full blur-[80px]`} />
      </div>

      <div className="relative max-w-md w-full">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 ${isDarkMode ? 'bg-[#1E9BD9]/10 border-[#1E9BD9]/20' : 'bg-[#1E9BD9]/5 border-[#1E9BD9]/10'} border px-4 py-2 rounded-full mb-4`}>
            <Sparkles className="h-4 w-4 text-[#1E9BD9]" />
            <span className="text-sm text-[#1E9BD9] font-medium">
              {isAr ? 'مرحباً بعودتك' : 'Welcome Back'}
            </span>
          </div>
          <h1 className={`text-4xl font-light mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}  data-testid="login-title">
            {t('auth.login')}
          </h1>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{t('auth.loginSubtitle')}</p>
        </div>

        <div className={`rounded-3xl p-8 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-[0_20px_60px_-15px_rgba(30,155,217,0.15)]'}`}>
          {/* Social sign-in — both buttons match at full-width, h-12 rounded-xl */}
          <div className="flex flex-col gap-2.5" data-testid="social-auth-wrapper">
            <div data-testid="google-login-wrapper" className="w-full">
              <GoogleSignInButton
                mode="auth"
                text="signin_with"
                locale={isAr ? 'ar' : 'en'}
                onSuccess={handleGoogleSuccess}
              />
            </div>
            <div data-testid="apple-login-wrapper" className="w-full">
              <AppleSignInButton
                mode="auth"
                locale={isAr ? 'ar' : 'en'}
                onSuccess={handleGoogleSuccess}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-wider">
              <span className={`px-3 ${isDarkMode ? 'bg-[#141414] text-gray-500' : 'bg-white text-gray-500'}`}>
                {isAr ? 'أو' : 'OR'}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`h-12 rounded-xl text-[15px] px-4 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'}`}
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                  {t('auth.password')}
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#1E9BD9] hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`h-12 rounded-xl text-[15px] px-4 pe-11 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'}`}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute inset-y-0 end-0 flex items-center pe-4 transition-colors ${isDarkMode ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? (isAr ? 'إخفاء كلمة المرور' : 'Hide password') : (isAr ? 'عرض كلمة المرور' : 'Show password')}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white text-base font-semibold shadow-lg shadow-[#1E9BD9]/25 hover:shadow-[#1E9BD9]/40 active:scale-[0.99] transition-all duration-150 mt-2"
              data-testid="login-submit-btn"
            >
              {loading ? t('auth.loggingIn') : t('auth.login')}
            </Button>
          </form>

          <div className="mt-7 text-center">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-[#1E9BD9] hover:underline font-semibold" data-testid="register-link">
                {t('auth.createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
